import { Link } from '@tanstack/react-router'
import { CircleAlert, GraduationCap, Plus, UserCheck } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Modal } from '@/components/ui/Modal'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { Veld } from '@/components/ui/Veld'
import { useGroepen, useMaakGroep } from '@/lib/groep-api'
import type { GroepKort, Traject } from '@/lib/groep-types'
import { useProduct } from '@/lib/me-api'
import { useSemesterKeuze } from '@/lib/semester-context'
import { cn } from '@/lib/utils'

const GEEN_TRAJECT = 'geen'

const TRAJECT_KLEUR: Record<Traject, string> = {
  VT: 'bg-soort-les/12 text-soort-les',
  MT: 'bg-soort-coaching/14 text-soort-coaching',
}

const TRAJECT_OPTIES = [
  { value: GEEN_TRAJECT, label: 'Geen traject' },
  { value: 'VT', label: 'Voortraject' },
  { value: 'MT', label: 'Maatwerktraject' },
]

export function GroepenPage() {
  const { semesterId, isLoading: semesterLaadt } = useSemesterKeuze()
  const query = useGroepen(semesterId)
  const maakGroep = useMaakGroep(semesterId)
  const magBewerken = useProduct() === 'beheer'
  const [open, setOpen] = useState(false)

  const laadt = semesterLaadt || query.isLoading

  return (
    <div className="space-y-6">
      <PageHeader
        title="Groepen"
        description="De lesreeksen per groep binnen het gekozen semester, en waar nog iets mist."
        actions={
          magBewerken ? (
            <button type="button" className="btn-primary" onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" />
              Groep toevoegen
            </button>
          ) : null
        }
      />

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : laadt ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-[112px] w-full rounded-card" />
          ))}
        </div>
      ) : !query.data || query.data.length === 0 ? (
        <EmptyState
          icon={GraduationCap}
          title="Nog geen groepen"
          description={
            magBewerken
              ? 'Voeg een groep toe om lesreeksen en lesmomenten vast te leggen.'
              : 'Groepen verschijnen zodra het rooster is ingelezen.'
          }
          action={
            magBewerken ? (
              <button type="button" className="btn-primary" onClick={() => setOpen(true)}>
                <Plus className="h-4 w-4" />
                Groep toevoegen
              </button>
            ) : null
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {query.data.map((groep) => (
            <Groepkaart key={groep.code} groep={groep} />
          ))}
        </div>
      )}

      <NieuweGroepModal
        open={open}
        onOpenChange={setOpen}
        bezig={maakGroep.isPending}
        onBewaar={(input) =>
          maakGroep.mutate(input, {
            onSuccess: () => setOpen(false),
          })
        }
      />
    </div>
  )
}

function Groepkaart({ groep }: { groep: GroepKort }) {
  const start =
    groep.startJaar !== null
      ? `start ${String(groep.startMaand ?? 1).padStart(2, '0')}-${groep.startJaar}`
      : null

  return (
    <Link
      to="/groep/$code"
      params={{ code: groep.code }}
      className="flex flex-col gap-1 rounded-card bg-surface p-4 shadow-card transition-all duration-150 ease-spring hover:-translate-y-px hover:bg-fill-1"
    >
      <span className="flex items-baseline justify-between gap-3">
        <span className="truncate text-sm font-medium text-ink">{groep.code}</span>
        <span
          className={cn(
            'shrink-0 rounded-pill px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em]',
            groep.traject ? TRAJECT_KLEUR[groep.traject] : 'bg-fill-2 text-ink-faint',
          )}
        >
          {groep.trajectLabel ?? 'Onbekend'}
        </span>
      </span>

      <span className="text-xs text-ink-faint">
        {[
          `${groep.aantalLesreeksen} ${groep.aantalLesreeksen === 1 ? 'lesreeks' : 'lesreeksen'}`,
          start,
        ]
          .filter(Boolean)
          .join(' · ')}
      </span>

      <span className="num mt-2 text-left text-sm text-ink-soft">
        {groep.leswekenInSemester} lesweken in dit semester
      </span>

      <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
        {groep.leswekenInSemester === 0 ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-status-onderbezet">
            <CircleAlert className="h-3.5 w-3.5" strokeWidth={1.85} />
            Geen lesweken gepland
          </span>
        ) : null}
        {groep.lesmomentenZonderMedewerker > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-status-onderbezet">
            <CircleAlert className="h-3.5 w-3.5" strokeWidth={1.85} />
            {groep.lesmomentenZonderMedewerker} zonder docent
          </span>
        ) : null}
        {groep.heeftMentor ? (
          <span className="inline-flex items-center gap-1 text-xs text-ink-faint">
            <UserCheck className="h-3.5 w-3.5" strokeWidth={1.85} />
            Mentor
          </span>
        ) : null}
        {!groep.actief ? <span className="text-xs text-ink-faint">Niet actief</span> : null}
      </span>
    </Link>
  )
}

interface NieuweGroepModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  bezig: boolean
  onBewaar: (input: {
    code: string
    traject: Traject | null
    startJaar?: number
    startMaand?: number
  }) => void
}

function NieuweGroepModal({ open, onOpenChange, bezig, onBewaar }: NieuweGroepModalProps) {
  const [code, setCode] = useState('')
  const [traject, setTraject] = useState(GEEN_TRAJECT)
  const [startJaar, setStartJaar] = useState('')
  const [startMaand, setStartMaand] = useState('')

  function verstuur(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault()
    if (code.trim() === '') return

    onBewaar({
      code: code.trim(),
      traject: traject === GEEN_TRAJECT ? null : (traject as Traject),
      ...(startJaar !== '' ? { startJaar: Number(startJaar) } : {}),
      ...(startMaand !== '' ? { startMaand: Number(startMaand) } : {}),
    })
  }

  return (
    <Modal
      open={open}
      onOpenChange={(volgende) => {
        onOpenChange(volgende)
        if (!volgende) {
          setCode('')
          setTraject(GEEN_TRAJECT)
          setStartJaar('')
          setStartMaand('')
        }
      }}
      title="Groep toevoegen"
      description="De code is hoe de groep in het rooster heet."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={() => onOpenChange(false)}>
            Annuleren
          </button>
          <button type="submit" form="nieuwe-groep" className="btn-primary" disabled={bezig}>
            Toevoegen
          </button>
        </>
      }
    >
      <form id="nieuwe-groep" onSubmit={verstuur} className="space-y-4">
        <Veld label="Code">
          <input
            className="field"
            value={code}
            onChange={(gebeurtenis) => setCode(gebeurtenis.target.value)}
            placeholder="VT01-2609"
            required
          />
        </Veld>

        <Veld label="Traject">
          <Select value={traject} onValueChange={setTraject} options={TRAJECT_OPTIES} />
        </Veld>

        <div className="grid grid-cols-2 gap-3">
          <Veld label="Startjaar">
            <input
              className="field num"
              type="number"
              inputMode="numeric"
              value={startJaar}
              onChange={(gebeurtenis) => setStartJaar(gebeurtenis.target.value)}
              placeholder="2026"
            />
          </Veld>
          <Veld label="Startmaand">
            <input
              className="field num"
              type="number"
              inputMode="numeric"
              min={1}
              max={12}
              value={startMaand}
              onChange={(gebeurtenis) => setStartMaand(gebeurtenis.target.value)}
              placeholder="9"
            />
          </Veld>
        </div>
      </form>
    </Modal>
  )
}
